import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import Utils from '../utils/utils';
import {urlSearchParams} from '../baseUrls';
import {updateUserState} from '../redux/user/userSlice';
import {
  AI_CALL_AVAILABILITY_RESTORED_KEY,
  AI_CALL_FROM_QUERY_VALUE,
  AI_CALL_TAB_CLOSE_GRACE_MS,
  AI_CALL_TAB_CLOSED_KEY,
  AI_CALL_TAB_CLOSED_MAX_AGE_MS,
  claimAiCallTabClosed,
  endAgentCallAfterTabClosed,
  notifyParentTabAvailabilityRestored,
  parseAiCallTabClosed,
  readAiCallTabClosed,
  setAgentAvailableAgain,
} from '../utils/aiCallSession';

/**
 * Runs in the tabs that stayed open when the AI-call inspection tab was opened. Once that tab has
 * been gone for {@link AI_CALL_TAB_CLOSE_GRACE_MS} without coming back, ends the call it was for
 * and sets the agent Available again. A refresh fires the same `pagehide` as a close and
 * withdraws the request when it reloads, so it touches neither.
 *
 * The tab that does this tells the rest via {@link AI_CALL_AVAILABILITY_RESTORED_KEY} so their
 * navbars follow.
 */
export function useAiCallParentTabAvailability() {
  const dispatch = useDispatch();
  const acceptAiCall = useSelector((state) => state.user?.acceptAiCall);
  const userId = useSelector((state) => state.user?.id);

  useEffect(() => {
    if (!acceptAiCall) {
      return undefined;
    }

    const fromAiCall = Utils.getUrlParameter(urlSearchParams.fromAiCall);
    if (fromAiCall === AI_CALL_FROM_QUERY_VALUE) {
      return undefined;
    }

    const timers = new Map();

    const scheduleEndCall = (closed) => {
      if (!closed || timers.has(closed.token)) {
        return;
      }
      if (userId && closed.userId && closed.userId !== String(userId)) {
        return;
      }
      const age = Date.now() - closed.at;
      if (age > AI_CALL_TAB_CLOSED_MAX_AGE_MS) {
        return;
      }
      const timer = setTimeout(async () => {
        timers.delete(closed.token);
        // Gone means a refresh withdrew it or another tab already took it.
        if (!claimAiCallTabClosed(closed.token)) {
          return;
        }
        const outcome = await endAgentCallAfterTabClosed(closed);
        if (outcome === 'ended') {
          // The server's `ai-call-ended` clears the navbar in every tab; this just says why.
          dispatch(updateUserState({aiOpenCall: null}));
          toast('Your AI call was ended because its tab was closed.', {type: 'info'});
        }
        // On a different call now, or unable to tell: leave availability where it is.
        if (outcome !== 'ended' && outcome !== 'no-call') {
          return;
        }
        try {
          await setAgentAvailableAgain();
          dispatch(updateUserState({aiAvailable: true}));
          notifyParentTabAvailabilityRestored();
        } catch {
          toast('Could not set you Available again. Please use the navbar checkbox.', {type: 'error'});
        }
      }, Math.max(0, AI_CALL_TAB_CLOSE_GRACE_MS - age));
      timers.set(closed.token, timer);
    };

    const onStorage = (event) => {
      if (event.key === AI_CALL_TAB_CLOSED_KEY) {
        scheduleEndCall(parseAiCallTabClosed(event.newValue));
        return;
      }
      if (event.key !== AI_CALL_AVAILABILITY_RESTORED_KEY || event.newValue == null) {
        return;
      }
      dispatch(updateUserState({aiAvailable: true}));
    };

    // A close that happened while no other tab was open is picked up by the next one to load.
    scheduleEndCall(readAiCallTabClosed());

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [dispatch, acceptAiCall, userId]);
}
